import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Space } from '../../../components/Space'
import { useViewportSize } from '../../../hooks'
import { Icon } from '../../../components/Icons'
import { Flex } from '../../../components/Flex'
import { Text, Title } from '../../../components/Text'
import { Box } from '../../../components/Box'
import { isUserLoggedIn } from '../../../redux/user/selectors'
import { formatPath } from '../../../utils/routes'
import { createTFunction } from '../../../utils/helpers'
import { Button } from '../../../components/Button'
import { getCmsLoginUrl, OfficialDocuments } from '../../../redux/config'
import { CompanyDetailActions, isCompanyLoading } from '../../../redux/companyDetail'
import { RootState } from '../../../redux/types'

import { ReportContainer, ReportActionsContainer, ReportActions } from './styled'

export type DocumentsInfoProps = {
  companyIdentifier: string
  documentsInfoData?: OfficialDocuments
  mode: 'TriggerLoad' | 'Promo'
}

export const DocumentsInfo = ({ companyIdentifier, documentsInfoData, mode }: DocumentsInfoProps) => {
  const { t } = useTranslation(['companyDetail', 'core'])
  const tcr = createTFunction('companyDetail:Reports.', t)
  const tc = createTFunction('core:', t)

  const dispatch = useDispatch()
  const viewPort = useViewportSize()
  const loggedIn = useSelector(isUserLoggedIn)
  const loginUrl = useSelector(getCmsLoginUrl)
  const companyLoading = useSelector((state: RootState) => isCompanyLoading(state, { companyIdentifier }))
  const triggerLoadDescription = mode === 'TriggerLoad' ? documentsInfoData?.infoBoxDescription : undefined
  const promoDescription = mode === 'Promo' ? documentsInfoData?.infoBoxPromo : undefined
  const boxDescription = (triggerLoadDescription || promoDescription) ?? ''

  return (
    <ReportContainer isPreferred={false} data-automation-id="officialDocuments">
      <Title level="medium">
        <Flex>
          <Icon type="FileTextOutlined" />
          <span className="report-name">{documentsInfoData?.infoBoxTitle}</span>
        </Flex>
      </Title>
      {loggedIn && (
        <Box pt="12px">
          <span className="desc" dangerouslySetInnerHTML={{ __html: boxDescription }}></span>
        </Box>
      )}
      <ReportActionsContainer
        borderTop={viewPort?.isDesktop || viewPort?.isWide}
        borderBottom={viewPort?.isMobile || viewPort?.isTablet}
        data-automation-id="documentsActionsContainer"
      >
        <ReportActions hasSample={false}>
          <Space space={2} alignItems="center" className="buttons">
            {!loggedIn && (
              <>
                <Text>{tcr('LogInPromptForDocuments')}</Text>
                <a href={formatPath(loginUrl, { returnUrl: window.location.href })}>
                  <Button variant="secondary" htmlType="button" data-automation-id="btnDocumentsLogin">
                    {tc('Login')}
                  </Button>
                </a>
              </>
            )}
            {loggedIn && mode === 'TriggerLoad' && (
              <>
                <Button
                  variant="secondary"
                  htmlType="submit"
                  loading={companyLoading}
                  disabled={companyLoading}
                  onClick={() =>
                    dispatch(CompanyDetailActions.loadCompany({ companyIdentifier, includeOfficialDocuments: true }))
                  }
                  data-automation-id="btnDocumentsDownload"
                >
                  {documentsInfoData?.infoBoxCtaTitle}
                </Button>
              </>
            )}
          </Space>
        </ReportActions>
      </ReportActionsContainer>
    </ReportContainer>
  )
}
